import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  Typography,
  Snackbar,
  Alert,
  Modal,
  Container,
} from '@mui/material';
import { styled } from '@mui/system';
import { FileText, Settings, User, Crown, Eye } from 'lucide-react';
import Handlebars from 'handlebars';
import { API_URI } from '../../constants/GlobalConstants';
import AddTemplateModal from './AddTemplateModal';
import TemplateGrid from './TemplateGrid';

// Define styled components
const DefaultBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -8,
  right: -8,
  zIndex: 10,
  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  borderRadius: '50%',
  width: 40,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
  animation: 'pulse 2s infinite',
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 0 0 0 rgba(16, 185, 129, 0.7)',
    },
    '70%': {
      boxShadow: '0 0 0 10px rgba(16, 185, 129, 0)',
    },
    '100%': {
      boxShadow: '0 0 0 0 rgba(16, 185, 129, 0)',
    },
  },
}));

const TypeChip = styled('div')(({ theme, templateType }) => ({
  borderRadius: '8px',
  fontWeight: 600,
  fontSize: '0.75rem',
  height: 28,
  background: templateType === 'Order-Based' 
    ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
    : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
  color: '#ffffff',
  border: 'none',
  padding: '0 12px',
  display: 'flex',
  alignItems: 'center',
  '& .icon': {
    color: '#ffffff',
    marginLeft: '8px',
  }
}));

const StyledCard = styled(Card)(({ theme, isDefault }) => ({
  position: 'relative',
  maxWidth: 380,
  margin: theme.spacing(2),
  borderRadius: '16px',
  overflow: 'visible',
  background: theme.palette.mode === 'dark'
    ? isDefault 
      ? 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)'
      : 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
    : isDefault
      ? 'linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #e2e8f0 100%)'
      : 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
  border: isDefault 
    ? `2px solid ${theme.palette.mode === 'dark' ? '#10b981' : '#059669'}`
    : `1px solid ${theme.palette.mode === 'dark' ? '#334155' : '#e5e7eb'}`,
  boxShadow: isDefault
    ? theme.palette.mode === 'dark' 
      ? '0 10px 25px rgba(16, 185, 129, 0.2), 0 4px 12px rgba(0, 0, 0, 0.3)'
      : '0 10px 25px rgba(5, 150, 105, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)'
    : theme.palette.mode === 'dark'
      ? '0 4px 12px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2)'
      : '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: isDefault
      ? theme.palette.mode === 'dark'
        ? '0 20px 40px rgba(16, 185, 129, 0.3), 0 8px 16px rgba(0, 0, 0, 0.4)'
        : '0 20px 40px rgba(5, 150, 105, 0.2), 0 8px 16px rgba(0, 0, 0, 0.15)'
      : theme.palette.mode === 'dark'
        ? '0 12px 24px rgba(0, 0, 0, 0.4), 0 6px 12px rgba(0, 0, 0, 0.3)'
        : '0 12px 24px rgba(0, 0, 0, 0.12), 0 6px 12px rgba(0, 0, 0, 0.08)',
  },
}));

const ActionButton = styled('button')(({ theme, variant }) => ({
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '10px 20px',
  minHeight: 44,
  fontSize: '0.875rem',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  ...(variant === 'contained' && {
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: '#ffffff',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
    border: 'none',
    '&:hover': {
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      boxShadow: '0 6px 16px rgba(59, 130, 246, 0.4)',
      transform: 'translateY(-2px)',
    },
    '&:disabled': {
      background: theme.palette.mode === 'dark' ? '#374151' : '#d1d5db',
      color: theme.palette.mode === 'dark' ? '#6b7280' : '#9ca3af',
      boxShadow: 'none',
    }
  }),
  ...(variant === 'outlined' && {
    borderColor: theme.palette.mode === 'dark' ? '#475569' : '#d1d5db',
    color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#374151',
    background: 'transparent',
    border: '1px solid',
    '&:hover': {
      borderColor: theme.palette.mode === 'dark' ? '#64748b' : '#9ca3af',
      background: theme.palette.mode === 'dark' ? 'rgba(71, 85, 105, 0.1)' : 'rgba(156, 163, 175, 0.1)',
      transform: 'translateY(-2px)',
    },
  })
}));

const StatsCard = styled('div')(({ theme }) => ({
  padding: '24px',
  borderRadius: '16px',
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
    : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  border: `1px solid ${theme.palette.mode === 'dark' ? '#334155' : '#e5e7eb'}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 4px 12px rgba(0, 0, 0, 0.3)'
    : '0 4px 12px rgba(0, 0, 0, 0.08)',
}));

const StyledPreviewModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '95%',
  maxWidth: 1400,
  height: '90vh',
  backgroundColor: theme.palette.mode === 'dark' ? '#0f172a' : '#fff',
  borderRadius: '20px',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 25px 50px rgba(0, 0, 0, 0.8)'
    : '0 25px 50px rgba(0, 0, 0, 0.3)',
  padding: '32px',
  overflow: 'hidden',
  border: `1px solid ${theme.palette.mode === 'dark' ? '#1e293b' : '#e5e7eb'}`,
}));

// Define Handlebars helpers
Handlebars.registerHelper('formatPrice', (value) => parseFloat(value || 0).toFixed(2));
Handlebars.registerHelper('eq', (a, b) => a === b);
Handlebars.registerHelper('dateFormat', (date) =>
  new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
);
Handlebars.registerHelper('multiply', (a, b) => parseFloat(a) * parseFloat(b));
Handlebars.registerHelper('divide', (a, b) => parseFloat(a) / parseFloat(b));
Handlebars.registerHelper('toUpperCase', (str) => {
  return str ? str.toUpperCase() : '';
});
Handlebars.registerHelper('toLowerCase', (str) => {
  return str ? str.toLowerCase() : '';
});

const dummyData = {
  documentType: 'Invoice',
  status: 'Unpaid',
  sender: {
    logo: 'https://via.placeholder.com/200x50?text=Logo',
    company: 'Example Corp',
    custom1: '123 Business St',
    custom2: 'contact@example.com',
    custom3: '555-123-4567',
    GST_HST_number: 'GST123456789',
  },
  client: {
    name: 'John Doe',
    clientAddress: {
      fullAddress: '456 Client Rd, Suite 100',
      street: '456 Client Rd',
      city: 'Client City',
      province: 'ON',
      postalCode: 'A1B 2C3',
      country: 'Canada',
    },
  },
  information: {
    number: 'INV-001',
    date: new Date().toISOString(),
    dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
  },
  salesperson: 'Jane Smith',
  products: [
    {
      type: 'Service',
      quantity: 2,
      description: 'Consulting Services',
      price: 500,
      taxes: [
        { type: 'GST', rate: 5, amount: 25 },
        { type: 'PST', rate: 7, amount: 35 },
      ],
      total: 560,
      materialDetails: [
        {
          materialName: 'Software License',
          quantity: 1,
          sellingPrice: 200,
          discountRate: 10,
          customCost: 180,
          discountReducedAmount: (200 * 1 * (10 / 100)).toFixed(2),
        },
      ],
    },
  ],
  subtotal: 500,
  detailedTaxes: [
    { type: 'GST', percentage: 5, value: 34 },
    { type: 'PST', percentage: 7, value: 35 },
  ],
  totalTax: 69,
  totalAmount: 569,
  transactions: [
    {
      date: new Date().toISOString(),
      modeOfPayment: 'Credit Card',
      amountApplied: 100,
    },
  ],
  bottomNotice: 'Thank you for your business!',
};

const InvoiceTemplateManager = () => {
  const [templates, setTemplates] = useState([]);
  const [defaultTemplates, setDefaultTemplates] = useState({ orderBased: null, custom: null });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const userLogin = useSelector((state) => state.userSignIn);
  const { userInfo } = userLogin;

  const fetchAppSettings = async () => {
    try {
      const appSettingsResponse = await axios.get(`${API_URI}/api/app/application-settings`, {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      });
      const { orderBasedTemplate, customTemplate } = appSettingsResponse.data.settings || appSettingsResponse.data;
      setDefaultTemplates({
        orderBased: orderBasedTemplate?._id || orderBasedTemplate || null,
        custom: customTemplate?._id || customTemplate || null,
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to fetch application settings',
        severity: 'error',
      });
      console.error('Error fetching app settings:', error);
    }
  };

  // Fetch templates and defaults on mount
  useEffect(() => {
    const fetchData = async () => {
      if (!userInfo?.token) {
        setSnackbar({
          open: true,
          message: 'User not authenticated',
          severity: 'error',
        });
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        // Fetch templates
        const templatesResponse = await axios.get(`${API_URI}/api/invoices/templates`, {
          headers: { Authorization: `Bearer ${userInfo.token}` },
        });
        setTemplates(templatesResponse.data.templates);

        // Fetch default templates
        await fetchAppSettings();
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Failed to fetch templates or settings',
          severity: 'error',
        });
        console.error('Error fetching data:', error);
      }
      setLoading(false);
    };

    fetchData();
  }, [userInfo?.token]);

  // Handle template selection
  const handleSelectTemplate = async (templateId, type) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URI}/api/invoices/select-template`,
        { templateId, type },
        { headers: { Authorization: `Bearer ${userInfo.token}` } }
      );
      await fetchAppSettings();
      setSnackbar({
        open: true,
        message: response.data.message,
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to select template',
        severity: 'error',
      });
    }
    setLoading(false);
  };

  // Handle preview modal open/close
  const handleOpenPreviewModal = (htmlContent) => {
    try {
      const template = Handlebars.compile(htmlContent);
      const renderedHtml = template(dummyData);
      setPreviewHtml(renderedHtml);
      setOpenPreviewModal(true);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to render preview. Check console for details.',
        severity: 'error',
      });
      console.error('Error rendering preview:', error);
    }
  };

  const handleClosePreviewModal = () => setOpenPreviewModal(false);

  // Handle template addition
  const handleTemplateAdded = (newTemplate) => {
    setTemplates((prev) => [...prev, newTemplate]);
  };

  // Handle snackbar
  const handleSnackbar = (snackbarState) => {
    setSnackbar(snackbarState);
  };

  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Find template name by ID
  const getTemplateName = (templateId) => {
    const template = templates.find((t) => t._id === templateId);
    return template ? template.name : templateId || 'Not Set';
  };

  // Map template type to defaultTemplates key
  const getDefaultKey = (type) => {
    const typeMap = {
      'Order-Based': 'orderBased',
      'Custom': 'custom',
    };
    return typeMap[type] || null;
  };

  const orderBasedCount = templates.filter(t => t.type === 'Order-Based').length;
  const customCount = templates.filter(t => t.type === 'Custom').length;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography 
              variant="h4" 
              sx={{ 
                color: (theme) => theme.palette.mode === 'dark' ? '#f1f5f9' : '#0f172a',
                fontWeight: 700,
                mb: 1,
                background: 'linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%)',  
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Invoice Templates
            </Typography>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: (theme) => theme.palette.mode === 'dark' ? '#94a3b8' : '#64748b',
                fontWeight: 400
              }}
            >
              Manage and customize your invoice templates
            </Typography>
          </Box>
          <AddTemplateModal
            userInfo={userInfo}
            onTemplateAdded={handleTemplateAdded}
            onSnackbar={handleSnackbar}
          />
        </Box>

        {/* Stats Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mb: 4 }}>
          <StatsCard>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                p: 2, 
                borderRadius: '12px', 
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: '#fff'
              }}>
                <FileText size={24} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: (theme) => theme.palette.mode === 'dark' ? '#f1f5f9' : '#0f172a' }}>
                  {templates.length}
                </Typography>
                <Typography variant="body2" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#94a3b8' : '#64748b' }}>
                  Total Templates
                </Typography>
              </Box>
            </Box>
          </StatsCard>

          <StatsCard>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                p: 2, 
                borderRadius: '12px', 
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#fff'
              }}>
                <Settings size={24} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: (theme) => theme.palette.mode === 'dark' ? '#f1f5f9' : '#0f172a' }}>
                  {orderBasedCount}
                </Typography>
                <Typography variant="body2" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#94a3b8' : '#64748b' }}>
                  Order-Based
                </Typography>
              </Box>
            </Box>
          </StatsCard>

          <StatsCard>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                p: 2, 
                borderRadius: '12px', 
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                color: '#fff'
              }}>
                <User size={24} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: (theme) => theme.palette.mode === 'dark' ? '#f1f5f9' : '#0f172a' }}>
                  {customCount}
                </Typography>
                <Typography variant="body2" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#94a3b8' : '#64748b' }}>
                  Custom
                </Typography>
              </Box>
            </Box>
          </StatsCard>
        </Box>

        {/* Current Defaults */}
        <StatsCard sx={{ mb: 4 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              color: (theme) => theme.palette.mode === 'dark' ? '#f1f5f9' : '#0f172a', 
              mb: 3,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Crown size={20} />
            Current Default Templates
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: '12px', 
              background: (theme) => theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
              border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'}`,
            }}>
              <Typography variant="subtitle2" sx={{ color: '#3b82f6', fontWeight: 600, mb: 1 }}>
                Order-Based Template
              </Typography>
              <Typography sx={{ 
                color: (theme) => theme.palette.mode === 'dark' ? '#e2e8f0' : '#1e293b',
                fontWeight: 500
              }}>
                {getTemplateName(defaultTemplates.orderBased)}
              </Typography>
            </Box>
            <Box sx={{ 
              p: 3, 
              borderRadius: '12px', 
              background: (theme) => theme.palette.mode === 'dark' ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.05)',
              border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(139, 92, 246, 0.3)' : 'rgba(139, 92, 246, 0.2)'}`,
            }}>
              <Typography variant="subtitle2" sx={{ color: '#8b5cf6', fontWeight: 600, mb: 1 }}>
                Custom Template
              </Typography>
              <Typography sx={{ 
                color: (theme) => theme.palette.mode === 'dark' ? '#e2e8f0' : '#1e293b',
                fontWeight: 500
              }}>
                {getTemplateName(defaultTemplates.custom)}
              </Typography>
            </Box>
          </Box>
        </StatsCard>
      </Box>

      {/* Templates Grid */}
      <TemplateGrid
        templates={templates}
        loading={loading}
        defaultTemplates={defaultTemplates}
        getTemplateName={getTemplateName}
        getDefaultKey={getDefaultKey}
        handleSelectTemplate={handleSelectTemplate}
        handleOpenPreviewModal={handleOpenPreviewModal}
      />

      {/* Preview Modal */}
      <Modal open={openPreviewModal} onClose={handleClosePreviewModal}>
        <StyledPreviewModalBox>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography
              variant="h5"
              sx={{ 
                color: (theme) => theme.palette.mode === 'dark' ? '#f1f5f9' : '#0f172a',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}
            >
              <Eye size={24} />
              Template Preview
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <ActionButton
                variant="outlined"
                onClick={handleClosePreviewModal}
                sx={{ minWidth: 100 }}
              >
                Close
              </ActionButton>
            </Box>
          </Box>
          
          <Box
            sx={{
              height: 'calc(100% - 80px)',
              borderRadius: '12px',
              overflow: 'hidden',
              border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? '#334155' : '#e5e7eb'}`,
              boxShadow: (theme) => theme.palette.mode === 'dark' 
                ? 'inset 0 2px 4px rgba(0, 0, 0, 0.3)'
                : 'inset 0 2px 4px rgba(0, 0, 0, 0.06)',
            }}
          >
            <iframe
              title="Template Preview"
              srcDoc={previewHtml}
              style={{ 
                width: '100%', 
                height: '100%', 
                border: 'none',
                borderRadius: '12px',
              }}
            />
          </Box>
        </StyledPreviewModalBox>
      </Modal>

      {/* Enhanced Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ 
            width: '100%',
            borderRadius: '12px',
            boxShadow: (theme) => theme.palette.mode === 'dark'
              ? '0 8px 24px rgba(0, 0, 0, 0.4)'
              : '0 8px 24px rgba(0, 0, 0, 0.12)',
            '& .MuiAlert-icon': {
              fontSize: '1.5rem'
            },
            '& .MuiAlert-message': {
              fontSize: '0.875rem',
              fontWeight: 500
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default InvoiceTemplateManager;